import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMenu } from 'react-icons/fi';
import {
  getHomeSummary,
  getMatchups,
  getUserLikes,
  getUserBracketLikes,
  getUserTierListLikes,
  getAllFollowing,
  listMyCommunities,
  logout as serverLogout,
  signOutLocally,
} from '../services/api';
import HomeSidebar from '../components/HomeSidebar';
import HomeCard, { deriveTags } from '../components/HomeCard';
import NotificationBell from '../components/NotificationBell';
import ProfilePic from '../components/ProfilePic';
import ThemeToggleItem from '../components/ThemeToggleItem';
import CreateMenu from '../components/CreateMenu';
import QuickVote from '../components/QuickVote';
import { track } from '../utils/analytics';
import '../styles/HomePage.css';
import '../components/NavigationBar.css';

const HomePage = () => {
  const [sortMode, setSortMode] = useState('latest');
  // typeFilter values are 'public' / 'following' / 'communities'.
  // Was 'all' / 'matchup' / 'bracket' historically — renamed to
  // reflect what the filters actually do now:
  //   public      → everything (matchups + brackets, anyone)
  //   following   → only items authored by users the viewer follows
  //   communities → only items scoped to a community the viewer is in
  const [typeFilter, setTypeFilter] = useState('public');
  const [categoryFilter, setCategoryFilter] = useState('all');
  // searchQuery used to back the inline home-page search input,
  // which no longer exists. The state stays so visibleItems' filter
  // branch keeps compiling — it's an always-empty string in
  // practice. Real search lives at /search via the magnifier
  // launcher in the top bar.
  const [searchQuery] = useState('');
  // Mobile sidebar drawer state. Below 768px the sidebar lives off-
  // canvas; this flag slides it in. Closing on a category/sort tap
  // keeps the drawer from staying open over the content the user
  // just filtered to.
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [matchups, setMatchups] = useState([]);
  const [brackets, setBrackets] = useState([]);
  const [tierLists, setTierLists] = useState([]);
  const [loading, setLoading] = useState(true);
  // Per-viewer "what have I already liked" sets. Fetched once on
  // mount (when authed) so each HomeCard can render its initial heart
  // state without an N+1 round-trip per card. Anon viewers stay with
  // empty sets — the like buttons short-circuit to /login on click.
  const [likedMatchupIds, setLikedMatchupIds] = useState(() => new Set());
  const [likedBracketIds, setLikedBracketIds] = useState(() => new Set());
  const [likedTierListIds, setLikedTierListIds] = useState(() => new Set());
  // Sets used by the Following / Communities tabs to filter the
  // combined feed. Populated once on mount (authed only); anon
  // viewers see the empty sets which make Following / Communities
  // tabs render their empty-state automatically.
  const [followingIds, setFollowingIds] = useState(() => new Set());
  // The Following tab's matchups, fetched server-side (following_only).
  // Filtering whatever the current sort loaded (24 latest / trending) hid
  // any follow who hadn't posted recently enough to be in that slice.
  const [followingMatchups, setFollowingMatchups] = useState([]);
  const [memberCommunityIds, setMemberCommunityIds] = useState(() => new Set());

  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const username = localStorage.getItem('username');

  // Top-of-funnel marker for anon visitors. Fires once on first
  // mount per session so we can measure the anon→signup funnel
  // starting from "saw the home page" rather than "site visit"
  // (autocapture covers raw pageview already).
  useEffect(() => {
    if (!userId) {
      track('anon_home_viewed');
    }
    // Empty deps — fire once per mount, not per filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One-shot fetch for the Following / Communities tab membership
  // sets. Runs in parallel with the likes fetch below. Anon viewers
  // skip — the sets stay empty so those tabs render their empty-state.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    Promise.allSettled([
      // ALL follows, paged — a bare getUserFollowing() stops at the
      // server's default 20, silently dropping follows #21 and up.
      getAllFollowing(userId),
      listMyCommunities({ limit: 200 }),
    ]).then(([followingRes, communitiesRes]) => {
      if (cancelled) return;
      if (followingRes.status === 'fulfilled') {
        // Each row is a UserSummary with `id` (public_id).
        const list = followingRes.value;
        const ids = new Set(
          (Array.isArray(list) ? list : []).map((u) => String(u.id ?? u.user_id ?? '')),
        );
        setFollowingIds(ids);
      }
      if (communitiesRes.status === 'fulfilled') {
        // listMyCommunities returns the communities the viewer is a
        // member of. Each row carries `id` (public_id).
        const data = communitiesRes.value?.data;
        const list = data?.communities ?? data?.response?.communities ?? data ?? [];
        const ids = new Set(
          (Array.isArray(list) ? list : []).map((c) => String(c.id ?? c.community_id ?? '')),
        );
        setMemberCommunityIds(ids);
      }
    });
    return () => { cancelled = true; };
  }, [userId]);

  // One-shot user-likes fetch. Runs in parallel; either failing is
  // non-fatal (the heart will just render in the unlit state and the
  // first click will reconcile via optimistic update + server call).
  // Authentication-gated — anon users skip both calls entirely.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    Promise.allSettled([
      getUserLikes(userId),
      getUserBracketLikes(userId),
      getUserTierListLikes(userId),
    ]).then(([matchupsRes, bracketsRes, tierListsRes]) => {
      if (cancelled) return;
      if (matchupsRes.status === 'fulfilled') {
        const data = matchupsRes.value?.data;
        const list = data?.likes ?? data?.response ?? data ?? [];
        const ids = new Set(
          (Array.isArray(list) ? list : []).map((l) => String(l.matchup_id ?? l.matchupId ?? l.id ?? '')),
        );
        setLikedMatchupIds(ids);
      }
      if (bracketsRes.status === 'fulfilled') {
        const data = bracketsRes.value?.data;
        const list = data?.likes ?? data?.response ?? data ?? [];
        const ids = new Set(
          (Array.isArray(list) ? list : []).map((l) => String(l.bracket_id ?? l.bracketId ?? l.id ?? '')),
        );
        setLikedBracketIds(ids);
      }
      if (tierListsRes.status === 'fulfilled') {
        const data = tierListsRes.value?.data;
        const list = data?.likes ?? data?.response ?? data ?? [];
        const ids = new Set(
          (Array.isArray(list) ? list : []).map((l) => String(l.tier_list_id ?? l.tierListId ?? l.id ?? '')),
        );
        setLikedTierListIds(ids);
      }
    });
    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Always fetch brackets from home summary (the only public bracket list endpoint)
        const summaryRes = await getHomeSummary(userId);
        const summaryData = summaryRes.data?.summary ?? summaryRes.data?.response ?? summaryRes.data ?? {};
        const homeBrackets = Array.isArray(summaryData.popular_brackets) ? summaryData.popular_brackets : [];
        // Tier lists are sort-independent (recent public, active) — the
        // summary returns the same set regardless of the matchup sort mode.
        setTierLists(Array.isArray(summaryData.tier_lists) ? summaryData.tier_lists : []);

        if (sortMode === 'latest') {
          const res = await getMatchups(1, 24);
          const payload = res.data?.matchups ?? res.data?.response ?? res.data ?? {};
          setMatchups(Array.isArray(payload) ? payload : Array.isArray(payload.matchups) ? payload.matchups : []);
          setBrackets(homeBrackets);
        } else if (sortMode === 'trending') {
          // Prefer hourly trending data; fall back to all-time popular if empty.
          const trending = Array.isArray(summaryData.trending_matchups) && summaryData.trending_matchups.length > 0
            ? summaryData.trending_matchups
            : Array.isArray(summaryData.popular_matchups) ? summaryData.popular_matchups : [];
          setMatchups(trending);
          setBrackets(homeBrackets);
        } else {
          // most-played: matchups with the most votes in the past hour
          const mostPlayed = Array.isArray(summaryData.most_played_matchups) && summaryData.most_played_matchups.length > 0
            ? summaryData.most_played_matchups
            : Array.isArray(summaryData.popular_matchups) ? summaryData.popular_matchups : [];
          setMatchups(mostPlayed);
          setBrackets(homeBrackets);
        }
      } catch (err) {
        console.error('HomePage fetch error:', err);
        setMatchups([]);
        setBrackets([]);
        setTierLists([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [sortMode, userId]);

  // Following tab: follows' matchups straight from the server, newest
  // first, independent of the sort mode (owner: "chronological order would
  // be best for just followers"). Anonymous viewers skip — the tab renders
  // its empty state.
  useEffect(() => {
    if (typeFilter !== 'following' || !userId) return;
    let cancelled = false;
    getMatchups(1, 48, { followingOnly: true })
      .then((res) => {
        if (cancelled) return;
        const payload = res.data?.matchups ?? res.data?.response ?? res.data ?? {};
        setFollowingMatchups(Array.isArray(payload) ? payload : Array.isArray(payload.matchups) ? payload.matchups : []);
      })
      .catch((err) => {
        console.error('Following feed fetch failed', err);
        if (!cancelled) setFollowingMatchups([]);
      });
    return () => { cancelled = true; };
  }, [typeFilter, userId]);

  // Build combined item list with type tag.
  //
  // typeFilter semantics:
  //   public      → everything (interleaved, no membership filter)
  //   following   → items authored by users the viewer follows
  //                 (matchups + brackets both count)
  //   communities → items scoped to a community the viewer is a
  //                 member of (matchups + brackets both count)
  //
  // Anon viewers have empty followingIds / memberCommunityIds, so
  // the latter two tabs naturally render empty — handled by the
  // empty-state below.
  const allItems = useMemo(() => {
    const m = matchups.map((item) => ({ ...item, _type: 'matchup' }));
    // This-or-That (pick_one) brackets are intentionally kept OFF the home
    // feed — they're a lightweight play format, not a standalone debate, and
    // cluttered the feed. Full (scoring) brackets stay. Their live child
    // matchups still appear and are labeled "Bracket Match" with a link back
    // to the parent bracket (see HomeCard).
    const b = brackets
      .filter((item) => item.format !== 'pick_one')
      .map((item) => ({ ...item, _type: 'bracket' }));
    const t = tierLists.map((item) => ({ ...item, _type: 'tierlist' }));
    // Interleave once; per-tab filters apply to the combined stream
    // so a viewer's Following tab mixes matchups, brackets, and tier
    // lists they care about in chronological-ish order.
    const interleaved = [];
    const maxLen = Math.max(m.length, b.length, t.length);
    for (let i = 0; i < maxLen; i++) {
      if (i < m.length) interleaved.push(m[i]);
      if (i < b.length) interleaved.push(b[i]);
      if (i < t.length) interleaved.push(t[i]);
    }
    if (typeFilter === 'following') {
      // Matchups come from the server's following_only page; brackets and
      // tier lists are the summary's global set, so they keep the filter.
      const byFollows = (it) => {
        const author = it.author_id ?? it.bracket_author_id ?? it.author?.id;
        return author && followingIds.has(String(author));
      };
      const fm = followingMatchups.map((item) => ({ ...item, _type: 'matchup' }));
      const extras = [...b, ...t].filter(byFollows);
      return [...fm, ...extras].sort((x, y) =>
        String(y.created_at || '').localeCompare(String(x.created_at || '')),
      );
    }
    if (typeFilter === 'communities') {
      return interleaved.filter((it) => {
        const cid = it.community_id;
        return cid && memberCommunityIds.has(String(cid));
      });
    }
    // 'public' (default) — no membership filter applied.
    return interleaved;
  }, [matchups, brackets, tierLists, typeFilter, followingIds, memberCommunityIds, followingMatchups]);

  // Client-side search + category filter
  const visibleItems = useMemo(() => {
    let items = allItems;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter((item) => (item.title ?? '').toLowerCase().includes(q));
    }
    if (categoryFilter !== 'all') {
      items = items.filter((item) => {
        const backendTags = Array.isArray(item.tags) && item.tags.length > 0 ? item.tags : null;
        const tags = backendTags ?? deriveTags(item.title ?? '');
        return tags.includes(categoryFilter);
      });
    }
    return items;
  }, [allItems, searchQuery, categoryFilter]);

  const navigateToCreate = () => {
    if (!userId) { navigate('/login'); return; }
    navigate(`/users/${username || userId}/create-matchup`);
  };

  const navigateToCreateBracket = () => {
    if (!userId) { navigate('/login'); return; }
    navigate('/brackets/new');
  };

  const navigateToCreatePool = () => {
    if (!userId) { navigate('/login'); return; }
    navigate('/pools/new');
  };

  const navigateToCreateTierList = () => {
    if (!userId) { navigate('/login'); return; }
    navigate('/tierlists/new');
  };

  // Mirrors NavigationBar.handleLogout — best-effort server revoke,
  // always clear local auth + redirect to /login. Inlined here
  // because the home-topbar now hosts the nav actions itself instead
  // of mounting <NavigationBar />.
  const isAuthed = Boolean(localStorage.getItem('token'));
  const handleLogout = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try { await serverLogout(refreshToken); } catch { /* ignore */ }
    }
    signOutLocally();
    navigate('/login', { replace: true });
  };

  // Profile-pic dropdown menu. Avatar trigger; menu collapses Home /
  // Admin / Logout / View-profile so the topbar reads clean. Bell stays
  // outside as an unread-state indicator. Outside-click + Escape close.
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  useEffect(() => {
    if (!profileMenuOpen) return undefined;
    const onPointerDown = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setProfileMenuOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [profileMenuOpen]);

  const profileSlug = (username && username !== 'undefined') ? username : userId;
  const goAndClose = (path) => () => {
    setProfileMenuOpen(false);
    navigate(path);
  };
  const handleLogoutFromMenu = async () => {
    setProfileMenuOpen(false);
    await handleLogout();
  };

  // Filter taps from inside the mobile drawer should close it — the
  // user expects the result to be visible, not blocked by the drawer
  // they just used.
  const handleSortChangeMobile = (next) => { setSortMode(next); setMobileSidebarOpen(false); };
  const handleCategoryChangeMobile = (next) => { setCategoryFilter(next); setMobileSidebarOpen(false); };

  return (
    <div className="home-page">
      {/* Mobile drawer scrim. Click anywhere outside the sidebar to
          dismiss. Only renders when the drawer is open so it doesn't
          intercept clicks at all desktop sizes. */}
      {mobileSidebarOpen && (
        <div
          className="home-sidebar-scrim"
          role="presentation"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}
      <HomeSidebar
        sortMode={sortMode}
        onSortChange={handleSortChangeMobile}
        categoryFilter={categoryFilter}
        onCategoryChange={handleCategoryChangeMobile}
        mobileOpen={mobileSidebarOpen}
      />

      <div className="home-main">
        <div className="home-topbar">
          {/* Hamburger — opens the off-canvas sidebar on small screens.
              Hidden via CSS at desktop sizes so the topbar stays clean. */}
          <button
            type="button"
            className="home-topbar__menu"
            aria-label="Open menu"
            aria-expanded={mobileSidebarOpen}
            onClick={() => setMobileSidebarOpen(true)}
          >
            <FiMenu />
          </button>
          {/* Quick Vote — slots into where the old search input used
              to live. The widget always renders something in this
              slot (loading skeleton → vote buttons → result → empty-
              state placeholder) so the top bar never has a visual
              hole. Server-side restriction: only text-only 2-item
              matchups are ever served here (no cover, no item
              images) — image matchups don't survive being reduced
              to two text buttons. */}
          <QuickVote />

          {/* Universal-search launcher. Sits as a peer to the input
              instead of inside it — the input live-filters the
              visible feed as you type, but the magnifier explicitly
              opens the full /search results page (matchups, brackets,
              communities, people). Two distinct intents, two
              affordances: type-here vs. take-me-to-search. The icon
              button is the discoverable "Explore" entry that survives
              even when the user doesn't realise the input itself
              escalates on Enter. */}
          <button
            type="button"
            className="home-topbar__search-launcher"
            aria-label="Open universal search"
            title="Search everything"
            onClick={() => navigate('/search')}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </button>

          {/* CREATE zone — single primary CTA. The previous pair of
              "+ Create Matchup" + "+ Create Bracket" buttons doubled
              up the primary affordance (Hick's Law), so they're now
              folded into a single dropdown. Auth gating + URL
              resolution still happens in the parent handlers so anon
              users keep getting bounced to /login. */}
          <div className="home-topbar__create">
            <CreateMenu
              onMatchup={navigateToCreate}
              onBracket={navigateToCreateBracket}
              onPool={navigateToCreatePool}
              onTierList={navigateToCreateTierList}
            />
          </div>

          {/* ACCOUNT zone. Authed users see [bell] [profile▾] with
              Home / Admin / Logout / View profile collapsed into the
              profile dropdown. Bell stays visible because its unread
              count is the whole point — burying it would defeat the
              indicator. Anon users see Sign in / Sign up since there's
              nothing to dropdown into. The CSS adds a border-left +
              padding-left to this zone so Gestalt grouping separates
              identity actions from the Create CTA. */}
          <div className="home-topbar__actions">
            {isAuthed ? (
              <>
                <NotificationBell />
                <div className="home-profile-menu" ref={profileMenuRef}>
                  <button
                    type="button"
                    className="home-profile-menu__trigger"
                    aria-haspopup="menu"
                    aria-expanded={profileMenuOpen}
                    onClick={() => setProfileMenuOpen((v) => !v)}
                  >
                    {userId && <ProfilePic userId={userId} size={44} />}
                  </button>

                  {profileMenuOpen && (
                    <div className="home-profile-menu__panel" role="menu">
                      {profileSlug && (
                        <button
                          type="button"
                          className="home-profile-menu__item"
                          role="menuitem"
                          onClick={goAndClose(`/users/${profileSlug}`)}
                        >
                          View profile
                        </button>
                      )}
                      {localStorage.getItem('isAdmin') === 'true' && (
                        <button
                          type="button"
                          className="home-profile-menu__item"
                          role="menuitem"
                          onClick={goAndClose('/admin')}
                        >
                          Admin
                        </button>
                      )}
                      {/* Theme toggle — same control here as in the
                          NavigationBar avatar menu so the user has a
                          consistent place to switch appearance no
                          matter which page they're on. */}
                      <ThemeToggleItem
                        className="home-profile-menu__item"
                      />
                      <div className="home-profile-menu__divider" />
                      <button
                        type="button"
                        className="home-profile-menu__item home-profile-menu__item--danger"
                        role="menuitem"
                        onClick={handleLogoutFromMenu}
                      >
                        Log out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="navigation-bar__button navigation-bar__button--ghost"
                  onClick={() => navigate('/login')}
                >
                  Sign in
                </button>
                <button
                  type="button"
                  className="navigation-bar__button"
                  onClick={() => navigate('/register')}
                >
                  Sign up
                </button>
              </>
            )}
          </div>
        </div>

        <div className="home-filters">
          {[
            { value: 'public', label: 'Public' },
            { value: 'following', label: 'Following' },
            { value: 'communities', label: 'Communities' },
          ].map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={`home-filter-chip${typeFilter === value ? ' home-filter-chip--active' : ''}`}
              onClick={() => setTypeFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="home-card-grid">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="home-card home-card--skeleton">
                <div className="home-card__header">
                  <div className="home-skeleton-circle" />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    <div className="home-skeleton-line" style={{ width: '40%' }} />
                    <div className="home-skeleton-line" style={{ width: '25%', height: '10px' }} />
                  </div>
                </div>
                <div className="home-card__caption">
                  <div className="home-skeleton-line home-skeleton-line--title" />
                  <div className="home-skeleton-line home-skeleton-line--short" />
                </div>
                <div className="home-card__media home-card__media--skeleton" />
                <div className="home-card__actions">
                  <div className="home-card__action-row">
                    <div className="home-skeleton-line" style={{ width: '20%' }} />
                    <div className="home-skeleton-line" style={{ width: '20%' }} />
                    <div className="home-skeleton-line" style={{ width: '20%' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="home-empty">
            {/* Tab-specific empty states. Following / Communities can
                fail for two reasons — viewer isn't authed (no follows
                / memberships to filter against) or authed-with-empty
                sets (followed nobody yet, no community joins). The
                copy speaks to each case rather than the generic
                "create the first one" CTA. */}
            {typeFilter === 'following' && (
              <>
                <p>
                  {userId
                    ? "Follow people to see their matchups and brackets here."
                    : "Sign in and follow creators to populate this tab."}
                </p>
                <button
                  type="button"
                  className="home-create-btn"
                  onClick={() => navigate(userId ? '/search' : '/login')}
                >
                  {userId ? 'Find people to follow' : 'Sign in'}
                </button>
              </>
            )}
            {typeFilter === 'communities' && (
              <>
                <p>
                  {userId
                    ? "Join a community to see its matchups and brackets here."
                    : "Sign in and join communities to populate this tab."}
                </p>
                <button
                  type="button"
                  className="home-create-btn"
                  onClick={() => navigate(userId ? '/communities' : '/login')}
                >
                  {userId ? 'Browse communities' : 'Sign in'}
                </button>
              </>
            )}
            {typeFilter === 'public' && (
              <>
                <p>No matchups found.</p>
                <button
                  type="button"
                  className="home-create-btn"
                  onClick={() => {
                    if (!userId) { navigate('/login'); return; }
                    navigateToCreate();
                  }}
                >
                  Create the first one
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="home-card-grid">
            {visibleItems.map((item) => {
              const isLikedByMe =
                item._type === 'bracket' ? likedBracketIds.has(String(item.id))
                : item._type === 'tierlist' ? likedTierListIds.has(String(item.id))
                : likedMatchupIds.has(String(item.id));
              return (
                <HomeCard
                  key={`${item._type}-${item.id}`}
                  item={item}
                  type={item._type}
                  initialLiked={isLikedByMe}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;
